import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { FloatingWhatsApp } from "@/components/site/FloatingWhatsApp";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import { rich } from "@/components/site/RichText";
import { listPosts, type PostCard } from "@/lib/blog.functions";
import { PostCardItem } from "@/routes/blog";

import portrait1 from "@/assets/kk-portrait-1.webp";
import portrait2 from "@/assets/kk-portrait-2.webp";
import portrait3 from "@/assets/kk-portrait-3.webp";
import proc1 from "@/assets/proc-1.webp";
import proc2 from "@/assets/proc-2.webp";
import proc3 from "@/assets/proc-3.webp";
import proc4 from "@/assets/proc-4.webp";
import proc5 from "@/assets/proc-5.webp";
import proc6 from "@/assets/proc-6.webp";
import proc7 from "@/assets/proc-7.webp";

export const Route = createFileRoute("/")({
  loader: () => listPosts(),
  head: () => ({
    meta: [{ property: "og:url", content: "https://cirurgiaplasticafoz.com.br/" }],
    links: [
      { rel: "canonical", href: "https://cirurgiaplasticafoz.com.br/" },
      { rel: "preload", as: "image", href: portrait1, fetchpriority: "high" },
    ],
  }),
  component: Index,
  errorComponent: () => (
    <div className="kk-section">
      <div className="kk-container">
        <h1 className="kk-h2">Dr. Kelson Kawamura</h1>
        <p className="kk-lead">Não foi possível carregar a página agora. Atualize em instantes.</p>
      </div>
    </div>
  ),
  notFoundComponent: () => (
    <div className="kk-section">
      <div className="kk-container">
        <h1 className="kk-h2">Página não encontrada</h1>
      </div>
    </div>
  ),

});


const WA_BASE = "https://wa.me/5545999175813";
const waLink = (msg: string) => `${WA_BASE}?text=${encodeURIComponent(msg)}`;
const WHATSAPP = waLink("Olá! Gostaria de agendar uma consulta com o Dr. Kelson Kawamura.");
const WHATSAPP_AVALIACAO = waLink("Olá! Gostaria de agendar uma avaliação com o Dr. Kelson Kawamura.");
const WHATSAPP_CONTATO = waLink("Olá! Vim pelo site do Dr. Kelson Kawamura e gostaria de mais informações.");

const PROCEDIMENTOS = [
  { title: "Mommy makeover", text: "Procedimentos combinados para restaurar contornos após a gestação.", image: proc1, path: "/especialidades/mommy-makeover-foz-do-iguacu" },
  { title: "Abdominoplastia", text: "Remove excessos abdominais e redefine o contorno corporal.", image: proc2, path: "/especialidades/abdominoplastia-foz-do-iguacu" },
  { title: "Lipoescultura", text: "Modela a silhueta com mais definição e harmonia.", image: proc3, path: "/especialidades/lipoescultura-foz-do-iguacu" },
  { title: "Mamoplastia de aumento", text: "Aumenta o volume das mamas com equilíbrio estético.", image: proc4, path: "/especialidades/mamoplastia-de-aumento-foz-do-iguacu" },
  { title: "Mastopexia com implantes", text: "Eleva as mamas e associa volume aos implantes.", image: proc5, path: "/especialidades/mastopexia-com-implantes-foz-do-iguacu" },
  { title: "Mamoplastia redutora", text: "Reduz o volume mamário, priorizando conforto e proporção.", image: proc6, path: "/especialidades/mamoplastia-redutora-foz-do-iguacu" },
  { title: "Rinoplastia", text: "Refina o nariz em equilíbrio com o rosto.", image: proc7, path: "/especialidades/rinoplastia-foz-do-iguacu" },
] as const;


const QUEIXAS = [
  "Flacidez abdominal e excesso de pele",
  "Gordura localizada e falta de definição corporal",
  "Alterações nas mamas, como queda, excesso ou falta de volume",
  "Insatisfação com o formato do nariz ou dificuldade para respirar",
  "Mudanças corporais após gestação",
];

const ETAPAS = [
  { n: "01", t: "Escuta inicial e anamnese", d: "Conversa atenta para entender histórico, queixas e expectativas." },
  { n: "02", t: "Avaliação clínica e física", d: "Exame minucioso para avaliar indicações e **segurança**." },
  { n: "03", t: "Registro fotográfico", d: "Documentação para planejamento cirúrgico individualizado." },
  { n: "04", t: "Possibilidades e limites", d: "Conversa transparente sobre o que é técnico e realista." },
  { n: "05", t: "Orientações completas", d: "Cirurgia, anestesia, cicatrizes, riscos, cuidados e **pós-operatório**." },
];

const CURRICULO = [
  "Graduação em Medicina pela Universidade do Oeste de Santa Catarina (UNOESC)",
  "Residência em Cirurgia Plástica no Hospital das Clínicas da UFPE (HC-UFPE)",
  "Membro da Sociedade Brasileira de Cirurgia Plástica (SBCP)",
  "Participante do Dallas Rhinoplasty Meeting — Dallas, Texas",
];

const FAQ = [
  { q: "Onde o Dr. Kelson Kawamura atende?", a: "O atendimento é realizado em Foz do Iguaçu - PR, na Rua Marechal Floriano Peixoto, 960, Edifício Torre Marechal, Sala 86." },
  { q: "O Dr. Kelson atende por telemedicina?", a: "Sim, há possibilidade de atendimento presencial e online, conforme a necessidade de cada caso." },
  { q: "Como funciona a primeira consulta?", a: "A **primeira consulta** envolve anamnese, avaliação clínica, avaliação física, registro fotográfico e conversa sobre possibilidades cirúrgicas, cuidados, riscos, cicatrizes e **pós-operatório**." },
  { q: "Quais procedimentos são realizados?", a: "Entre os principais procedimentos estão Mommy Makeover, Abdominoplastia, Lipoescultura, Rinoplastia, Prótese de mama, Mamoplastia redutora e Mastopexia com prótese." },
  { q: "Atende convênio?", a: "Não. O atendimento é particular." },
  { q: "Como agendar?", a: "O agendamento pode ser feito pelo WhatsApp informado no site." },
];

const Icon = ({ d }: { d: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

function FaqItem({ question, answer, isOpen, onToggle }: { question: string; answer: string; isOpen: boolean; onToggle: () => void }) {
  return (
    <div className={`kk-faq__item${isOpen ? " is-open" : ""}`}>
      <button
        type="button"
        className="kk-faq__summary"
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        <span>{question}</span>
        <span className="kk-faq__icon" aria-hidden="true">+</span>
      </button>
      <div className="kk-faq__content" role="region">
        <p>{rich(answer)}</p>
      </div>
    </div>
  );
}

function Index() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const { posts } = Route.useLoaderData() as { posts: PostCard[] };
  const ultimosPosts = posts.slice(0, 3);

  return (
    <>
      <Header />
      <main id="inicio">
        {/* HERO */}
        <section className="kk-section kk-gradient-hero kk-hero-section">
          <div className="kk-container kk-hero__grid kk-fade-up">
            <div>
              <span className="kk-eyebrow">Cirurgia Plástica · Foz do Iguaçu</span>
              <h1 className="kk-h1">Cirurgião plástico em Foz do Iguaçu</h1>
              <p className="kk-lead">
                Cirurgia plástica com olhar técnico, <strong>atendimento humanizado</strong> e foco em <strong>resultados naturais</strong>.
              </p>
              <div className="kk-hero__cta-row">
                <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="kk-button-primary">
                  Agendar pelo WhatsApp <WhatsAppIcon />
                </a>
                <a href="#consulta" className="kk-button-secondary">
                  Conhecer o atendimento
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </a>
              </div>
              <div className="kk-badge-row">
                <span className="kk-badge">CRM 32174</span>
                <span className="kk-badge">RQE 24787</span>
                <span className="kk-badge">Membro SBCP</span>
              </div>
            </div>
            <div className="kk-image-editorial kk-image-editorial--hero">
              <img src={portrait1} alt="Dr. Kelson Kawamura, cirurgião plástico em Foz do Iguaçu" fetchPriority="high" decoding="async" />
            </div>
          </div>
        </section>

        {/* PROCEDIMENTOS */}
        <section className="kk-section" id="procedimentos">
          <div className="kk-container">
            <div style={{ marginBottom: 64 }}>
              <span className="kk-eyebrow">Procedimentos</span>
              <h2 className="kk-h2">Cirurgias plásticas em Foz do Iguaçu</h2>
              <hr className="kk-divider" />
              <p className="kk-lead" style={{ maxWidth: "none" }}>
                Procedimentos planejados de forma individualizada, sempre considerando <strong>segurança</strong>, proporções
                naturais e <strong>expectativas realistas</strong>.
              </p>
            </div>
            <div className="kk-grid kk-grid-4">
              {PROCEDIMENTOS.map((p) => (
                <article key={p.title} className="kk-card">
                  <div className="kk-card__image-placeholder" aria-hidden="true">
                    <img src={p.image} alt={`${p.title} — Dr. Kelson Kawamura, cirurgião plástico em Foz do Iguaçu`} loading="lazy" decoding="async" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                  <h3 className="kk-h3">{p.title}</h3>
                  <p className="kk-text">{rich(p.text)}</p>
                  <Link to={p.path} className="kk-card__link">
                    Ver procedimento <span aria-hidden="true">→</span>
                  </Link>
                </article>

              ))}
            </div>
          </div>
        </section>

        {/* SOBRE — integra "autoridade" */}
        <section className="kk-section kk-section--alt" id="sobre">
          <div className="kk-container kk-two-col">
            <div className="kk-image-editorial" style={{ aspectRatio: "4 / 5" }}>
              <img src={portrait2} alt="Retrato do Dr. Kelson Kawamura" loading="lazy" decoding="async" />
            </div>
            <div>
              <span className="kk-eyebrow">Currículo</span>
              <h2 className="kk-h2">Dr. Kelson Kawamura - Cirurgião plástico</h2>
              <hr className="kk-divider" />
              <p className="kk-text">
                Com atendimento individualizado, escuta atenta e planejamento cirúrgico seguro, o Dr. Kelson
                Kawamura atua com foco em <strong>resultados naturais</strong> e harmônicos, respeitando as proporções,
                expectativas e características de cada paciente.
              </p>
              <p className="kk-text">
                Mais do que transformar, o objetivo é valorizar a beleza de forma equilibrada, com técnica,
                <strong>segurança</strong> e <strong>acompanhamento próximo</strong> em todas as etapas do pré ao pós-operatório.
              </p>

              <div className="kk-grid kk-grid-2" style={{ margin: "32px 0", gap: 32 }}>
                <div className="kk-stat">
                  <span className="kk-stat__num">8</span>
                  <span className="kk-stat__label">anos de experiência</span>
                </div>
                <div className="kk-stat">
                  <span className="kk-stat__num">4 mil+</span>
                  <span className="kk-stat__label">procedimentos realizados</span>
                </div>
              </div>

              <ul className="kk-bullets">
                {CURRICULO.map((c) => <li key={c}>{rich(c)}</li>)}
              </ul>

              <Link to="/dr-kelson-kawamura" className="kk-button-primary" style={{ marginTop: 28 }}>
                Saiba mais
              </Link>

              
            </div>
          </div>
        </section>

        {/* QUANDO PROCURAR — duas colunas com foto + CTA */}
        <section className="kk-section" id="curriculo">
          <div className="kk-container kk-two-col">
            <div>
              <span className="kk-eyebrow">Avaliação especializada</span>
              <h2 className="kk-h2">Quando procurar um cirurgião plástico?</h2>
              <hr className="kk-divider" />
              <p className="kk-lead">
                Algumas queixas podem indicar o momento de buscar uma <strong>avaliação especializada</strong>, sempre com
                orientação médica e análise individual.
              </p>
              <ul className="kk-bullets">
                {QUEIXAS.map((q) => <li key={q}>{rich(q)}</li>)}
              </ul>
              <a href={WHATSAPP_AVALIACAO} target="_blank" rel="noopener noreferrer" className="kk-button-primary" style={{ marginTop: 16 }}>
                Agendar avaliação <WhatsAppIcon />
              </a>
            </div>
            <div className="kk-image-editorial" style={{ aspectRatio: "3 / 4" }}>
              <img src={portrait3} alt="Dr. Kelson Kawamura em consulta de avaliação especializada" loading="lazy" decoding="async" />
            </div>
          </div>
        </section>

        {/* PRIMEIRA CONSULTA — centralizado */}
        <section className="kk-section kk-section--alt" id="consulta">
          <div className="kk-container">
            <div style={{ marginBottom: 56, textAlign: "center" }}>
              <span className="kk-eyebrow">Atendimento</span>
              <h2 className="kk-h2">Como funciona a primeira consulta</h2>
              <hr className="kk-divider" style={{ margin: "0 auto 24px" }} />
              <p className="kk-lead" style={{ margin: "0 auto", maxWidth: "none" }}>
                A consulta é um momento de avaliação cuidadosa, conversa transparente e construção de um
                <strong>plano cirúrgico</strong> seguro e realista.
              </p>
            </div>
            <div className="kk-grid kk-grid-5">
              {ETAPAS.map((e) => (
                <article key={e.n} className="kk-card kk-card--numbered">
                  <span className="kk-card__num">{e.n}</span>
                  <h3 className="kk-h3" style={{ fontSize: 20 }}>{e.t}</h3>
                  <p className="kk-text" style={{ margin: 0 }}>{rich(e.d)}</p>
                </article>
              ))}
            </div>
            <div style={{ marginTop: 48, textAlign: "center" }}>
              <a href={WHATSAPP_AVALIACAO} target="_blank" rel="noopener noreferrer" className="kk-button-primary">
                Agendar avaliação <WhatsAppIcon />
              </a>
            </div>
          </div>
        </section>

        {/* BLOG — últimos artigos */}
        <section className="kk-section">
          <div className="kk-container">
            <div style={{ textAlign: "center", marginBottom: 8 }}>
              <span className="kk-eyebrow">Blog</span>
              <h2 className="kk-h2">Artigos mais recentes</h2>
              <hr className="kk-divider" style={{ margin: "0 auto 24px" }} />
              <p className="kk-lead" style={{ margin: "0 auto", maxWidth: "none" }}>
                Conteúdo do Dr. Kelson Kawamura sobre indicações, preparo e <strong>pós-operatório</strong>.
              </p>
            </div>

            <div className="kk-post-grid">
              {ultimosPosts.map((post) => (
                <PostCardItem key={post.slug} post={post} />
              ))}
            </div>

            <div style={{ marginTop: 48, textAlign: "center" }}>
              <Link to="/blog" className="kk-button-secondary">Ver todos os artigos</Link>
            </div>
          </div>
        </section>

        <div className="kk-container">
          <hr className="kk-divider" style={{ margin: "0 auto" }} />
        </div>




        {/* CONSULTÓRIO */}

        <section className="kk-section" id="consultorio">
          <div className="kk-container">
            <div style={{ marginBottom: 64, textAlign: "center" }}>
              <span className="kk-eyebrow">Consultório</span>
              <h2 className="kk-h2">Atendimento em Foz do Iguaçu</h2>
              <hr className="kk-divider" style={{ margin: "0 auto 24px" }} />
              <p className="kk-lead" style={{ margin: "0 auto", maxWidth: "none" }}>
                O consultório está localizado em uma região central de Foz do Iguaçu, em um dos prédios mais
                modernos do Paraná, com estacionamento privativo, sala de espera confortável e estrutura
                pensada para conforto e <strong>segurança</strong>.
              </p>
            </div>

            <div className="kk-two-col" style={{ alignItems: "stretch" }}>
              <ul className="kk-contact-list" style={{ margin: 0 }}>
                <li>
                  <span className="kk-contact-icon">
                    <Icon d="M12 22s-7-7.58-7-12a7 7 0 1 1 14 0c0 4.42-7 12-7 12Z M12 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
                  </span>
                  <span>
                    Rua Marechal Floriano Peixoto, 960<br />
                    Edifício Torre Marechal, Sala 86<br />
                    Foz do Iguaçu — PR · CEP 85851-020
                  </span>
                </li>
                <li>
                  <span className="kk-contact-icon"><WhatsAppIcon size={20} /></span>
                  <a href={WHATSAPP} target="_blank" rel="noopener noreferrer">+55 45 99917-5813 (WhatsApp)</a>
                </li>
                <li>
                  <span className="kk-contact-icon">
                    <Icon d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92Z" />
                  </span>
                  <a href="tel:+5545999175813">+55 45 99917-5813</a>
                </li>
                <li>
                  <span className="kk-contact-icon">
                    <Icon d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2Z M22 6 12 13 2 6" />
                  </span>
                  <a href="mailto:drkelsonkawamura@gmail.com">drkelsonkawamura@gmail.com</a>
                </li>
                <li>
                  <span className="kk-contact-icon">
                    <Icon d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Z M16 11.4a4 4 0 1 1-7.9 1.2A4 4 0 0 1 16 11.4Z M17.5 6.5h.01" />
                  </span>
                  <a href="https://instagram.com/drkelsonkawamura" target="_blank" rel="noopener noreferrer">
                    @drkelsonkawamura
                  </a>
                </li>
                <li>
                  <span className="kk-contact-icon">
                    <Icon d="M12 8v4l3 2 M12 22a10 10 0 1 1 0-20 10 10 0 0 1 0 20Z" />
                  </span>
                  <span>Seg a Sex · 08h às 12h e 14h às 18h · Sáb · 08h às 12h</span>
                </li>
              </ul>

              <div className="kk-map-embed" style={{ aspectRatio: "auto", minHeight: 420 }}>
                <iframe
                  title="Mapa do consultório do Dr. Kelson Kawamura"
                  src="https://www.google.com/maps?q=Rua%20Marechal%20Floriano%20Peixoto%2C%20960%2C%20Foz%20do%20Igua%C3%A7u%2C%20PR&output=embed"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="kk-section kk-section--alt">
          <div className="kk-container">
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <span className="kk-eyebrow">Dúvidas frequentes</span>
              <h2 className="kk-h2">Perguntas frequentes</h2>
            </div>
            <div className="kk-faq">
              {FAQ.map((f, i) => (
                <FaqItem
                  key={f.q}
                  question={f.q}
                  answer={f.a}
                  isOpen={openFaqIndex === i}
                  onToggle={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* CTA FINAL — full-width */}
        <section className="kk-cta-fullwidth" id="contato">
          <div className="kk-container" style={{ textAlign: "center", padding: "0 24px" }}>
            <span className="kk-eyebrow">Contato</span>
            <h2 className="kk-h2">Agende sua consulta com cirurgião plástico em Foz do Iguaçu</h2>
            <p className="kk-lead" style={{ margin: "0 auto 32px" }}>
              Converse com a equipe e entenda o melhor caminho para uma <strong>avaliação individualizada</strong>.
            </p>
            <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
              <a href={WHATSAPP_CONTATO} target="_blank" rel="noopener noreferrer" className="kk-button-primary">
                Falar pelo WhatsApp <WhatsAppIcon />
              </a>
              <a href="https://instagram.com/drkelsonkawamura" target="_blank" rel="noopener noreferrer" className="kk-button-secondary">
                @drkelsonkawamura
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </>
  );
}
